/**
 * RHCoachingHumainDashboard.jsx
 * ==============================
 * 
 * Dashboard RH dédié au Coaching Humain Entreprise.
 * Onglets: Vue d'ensemble, Collaborateurs, Sessions, Budget
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/environment';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';
import {
  Users, BarChart3, Calendar, Euro, Plus, Loader2, Check, X,
  Upload, UserPlus, Clock, Target, TrendingUp, ArrowRight, FileText,
  Sparkles, Star, Search
} from 'lucide-react';

const API = getApiUrl();

function StatCard({ label, value, sub, icon: Icon, color = '#22c55e' }) {
  return (
    <div className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div>
          <p className="text-xl font-light text-[#111827]">{value}</p>
          <p className="text-xs text-[#6B7280]">{label}</p>
          {sub && <p className="text-xs text-[#6B7280] mt-0.5">{sub}</p>}
        </div>
      </div>
    </div>
  );
}

export default function RHCoachingHumainDashboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  // Add collaborator dialog
  const [showAddCollab, setShowAddCollab] = useState(false);
  const [collabForm, setCollabForm] = useState({ name: '', email: '', sessions_allocated: 3 });
  const [addingCollab, setAddingCollab] = useState(false);

  // Bulk add
  const [bulkText, setBulkText] = useState('');

  // AI Matching
  const [matchNeed, setMatchNeed] = useState('');
  const [matchDate, setMatchDate] = useState('');
  const [matchResults, setMatchResults] = useState(null);
  const [matching, setMatching] = useState(false);

  const fetchPrograms = useCallback(async () => {
    try {
      const res = await fetch(`${API}/coaching-programs`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setPrograms(data.programs || []);
        // Auto-select first active or the one from URL
        const urlProgramId = searchParams.get('program_id');
        const active = data.programs?.filter(p => p.status === 'active' || p.payment_status === 'paid') || [];
        let programToFetch = null;
        if (urlProgramId) {
          const found = data.programs?.find(p => p.id === urlProgramId);
          if (found) programToFetch = found.id;
          else if (active.length > 0) programToFetch = active[0].id;
        } else if (active.length > 0) {
          programToFetch = active[0].id;
        }
        // Fetch full program details including collaborators
        if (programToFetch) {
          const detailRes = await fetch(`${API}/coaching-programs/${programToFetch}`, { headers: { Authorization: `Bearer ${token}` } });
          if (detailRes.ok) {
            const detailData = await detailRes.json();
            setSelectedProgram(detailData);
          }
        }
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [token, searchParams]);

  const fetchProgramDetail = useCallback(async (programId) => {
    try {
      const res = await fetch(`${API}/coaching-programs/${programId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setSelectedProgram(data);
      }
    } catch (err) { console.error(err); }
  }, [token]);

  useEffect(() => { fetchPrograms(); }, [fetchPrograms]);

  // Poll payment status if needed
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const payment = searchParams.get('payment');
    const programId = searchParams.get('program_id');
    if (payment === 'success' && programId) {
      const poll = async () => {
        try {
          const res = await fetch(`${API}/coaching-programs/${programId}`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            const data = await res.json();
            if (data.payment_status === 'paid') {
              setSelectedProgram(data);
              toast.success('Programme activé ! Vous pouvez maintenant ajouter vos collaborateurs.');
              fetchPrograms();
            }
          }
        } catch (err) { console.error(err); }
      };
      poll();
      const interval = setInterval(poll, 3000);
      setTimeout(() => clearInterval(interval), 20000);
      return () => clearInterval(interval);
    }
  }, [searchParams, token, fetchPrograms]);

  const addCollaborator = async () => {
    if (!selectedProgram || !collabForm.name || !collabForm.email) {
      toast.error('Remplissez tous les champs');
      return;
    }
    setAddingCollab(true);
    try {
      const res = await fetch(`${API}/coaching-programs/${selectedProgram.id}/collaborators`, {
        method: 'POST', headers,
        body: JSON.stringify({
          collaborators: [{ name: collabForm.name, email: collabForm.email, sessions_allocated: collabForm.sessions_allocated }],
        }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Erreur'); }
      const data = await res.json();
      toast.success(`${data.added} collaborateur(s) ajouté(s) — invitation envoyée`);
      setShowAddCollab(false);
      setCollabForm({ name: '', email: '', sessions_allocated: 3 });
      fetchProgramDetail(selectedProgram.id);
    } catch (err) { toast.error(err.message); }
    setAddingCollab(false);
  };

  const addBulkCollaborators = async () => {
    if (!selectedProgram || !bulkText.trim()) return;
    const lines = bulkText.trim().split('\n').filter(l => l.trim());
    const collaborators = [];
    for (const line of lines) {
      const parts = line.split(/[,;\t]+/).map(s => s.trim());
      if (parts.length >= 2) {
        collaborators.push({
          name: parts[0],
          email: parts[1],
          sessions_allocated: parseInt(parts[2]) || 3,
        });
      }
    }
    if (collaborators.length === 0) {
      toast.error('Format invalide. Utilisez: Nom, Email, NbSessions');
      return;
    }
    setAddingCollab(true);
    try {
      const res = await fetch(`${API}/coaching-programs/${selectedProgram.id}/collaborators`, {
        method: 'POST', headers,
        body: JSON.stringify({ collaborators }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Erreur'); }
      const data = await res.json();
      toast.success(`${data.added} collaborateur(s) importé(s)`);
      setBulkText('');
      fetchProgramDetail(selectedProgram.id);
    } catch (err) { toast.error(err.message); }
    setAddingCollab(false);
  };

  const runMatching = async () => {
    if (!matchNeed.trim()) { toast.error('Décrivez le besoin du collaborateur'); return; }
    setMatching(true);
    try {
      const res = await fetch(`${API}/coaching-programs/match-coaches`, {
        method: 'POST', headers,
        body: JSON.stringify({
          need: matchNeed,
          program_id: prog?.id || null,
          preferred_date: matchDate || null,
          limit: 5,
        }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Erreur'); }
      const data = await res.json();
      setMatchResults(data.coaches || []);
      if (data.coaches?.length === 0) toast.info('Aucun coach trouvé pour ces critères');
    } catch (err) { toast.error(err.message); }
    setMatching(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F7F4] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-green-400 animate-spin" />
      </div>
    );
  }

  // No programs yet
  if (programs.length === 0) {
    return (
      <div className="theme-light min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
            <Users className="w-8 h-8 text-green-400" />
          </div>
          <h2 className="text-2xl font-light text-[#111827] mb-3">Aucun programme actif</h2>
          <p className="text-[#6B7280] text-sm mb-8">Créez votre premier programme de coaching humain entreprise.</p>
          <button onClick={() => navigate('/subscription/enterprise/coaching-humain')}
            className="px-6 py-3 bg-green-500 text-[#111827] rounded-full font-medium text-sm hover:bg-green-400 transition-all inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Créer un programme
          </button>
        </div>
      </div>
    );
  }

  const prog = selectedProgram || programs[0];
  const collabs = prog?.collaborators || [];
  const sessionsRemaining = (prog?.total_sessions || 0) - (prog?.sessions_used || 0);
  const budgetRemaining = (prog?.budget_total || 0) - (prog?.budget_used || 0);
  const sessionsAllocated = prog?.sessions_allocated || 0;
  const progressPct = prog?.total_sessions ? Math.round((prog.sessions_used / prog.total_sessions) * 100) : 0;

  return (
    <div className="theme-light min-h-screen pt-16 sm:pt-8 pb-28 sm:pb-8 px-4" data-testid="rh-coaching-humain-dashboard">
      <div className="max-w-6xl mx-auto">
        <BackButton />

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <p className="uppercase text-xs tracking-[0.2em] text-green-400 mb-1">Dashboard RH — Coaching Humain</p>
            <h1 className="text-xl sm:text-2xl font-light text-[#111827]">{prog?.name || 'Programme'}</h1>
          </div>
          <div className="flex items-center gap-2">
            {prog?.status === 'active' && <Badge className="bg-green-500/10 text-green-400 border-green-500/20">Actif</Badge>}
            {prog?.status === 'pending_payment' && <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20">Paiement en attente</Badge>}
            {programs.length > 1 && (
              <select value={prog?.id || ''} onChange={(e) => {
                const p = programs.find(x => x.id === e.target.value);
                if (p) { setSelectedProgram(p); fetchProgramDetail(p.id); }
              }} className="px-3 py-1.5 bg-white border border-[#DDD9D0] rounded-xl text-zinc-300 text-xs">
                {programs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <div className="overflow-x-auto -mx-4 px-4 mb-6">
            <TabsList className="bg-white border border-[#E8E5DE] inline-flex w-auto min-w-max">
              <TabsTrigger value="overview" className="data-[state=active]:bg-[#111827] data-[state=active]:text-white whitespace-nowrap text-sm px-3 py-2">
                <BarChart3 className="w-4 h-4 mr-1.5" /> Vue d'ensemble
              </TabsTrigger>
              <TabsTrigger value="collaborators" className="data-[state=active]:bg-[#111827] data-[state=active]:text-white whitespace-nowrap text-sm px-3 py-2">
                <Users className="w-4 h-4 mr-1.5" /> Collaborateurs
                <span className="ml-1.5 text-[10px] bg-white/10 px-1.5 py-0.5 rounded">{collabs.length}</span>
              </TabsTrigger>
              <TabsTrigger value="sessions" className="data-[state=active]:bg-[#111827] data-[state=active]:text-white whitespace-nowrap text-sm px-3 py-2">
                <Calendar className="w-4 h-4 mr-1.5" /> Sessions
              </TabsTrigger>
              <TabsTrigger value="budget" className="data-[state=active]:bg-[#111827] data-[state=active]:text-white whitespace-nowrap text-sm px-3 py-2">
                <Euro className="w-4 h-4 mr-1.5" /> Budget
              </TabsTrigger>
              <TabsTrigger value="matching" className="data-[state=active]:bg-[#111827] data-[state=active]:text-white whitespace-nowrap text-sm px-3 py-2">
                <Sparkles className="w-4 h-4 mr-1.5" /> Orientation IA
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Overview */}
          <TabsContent value="overview">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <StatCard label="Sessions totales" value={prog?.total_sessions || 0} icon={Calendar} />
              <StatCard label="Sessions utilisées" value={prog?.sessions_used || 0} sub={`${progressPct}% consommé`} icon={TrendingUp} color="#D4AF37" />
              <StatCard label="Collaborateurs" value={collabs.length} sub={`${sessionsAllocated} sessions attribuées`} icon={Users} />
              <StatCard label="Budget restant" value={`${budgetRemaining.toLocaleString('fr-FR')}€`} sub={`sur ${(prog?.budget_total || 0).toLocaleString('fr-FR')}€`} icon={Euro} />
            </div>

            {/* Progress bar */}
            <div className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-5 mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[#111827] font-medium text-sm">Consommation du programme</h3>
                <span className="text-green-400 text-sm font-medium">{sessionsRemaining} sessions restantes</span>
              </div>
              <div className="w-full h-3 bg-[#F0EDE6] rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(progressPct, 100)}%` }} />
              </div>
              <div className="flex justify-between mt-2 text-xs text-[#6B7280]">
                <span>{prog?.sessions_used || 0} utilisées</span>
                <span>{sessionsAllocated} attribuées</span>
                <span>{prog?.total_sessions || 0} total</span>
              </div>
            </div>

            {/* Specialties */}
            {prog?.specialties_focus?.length > 0 && (
              <div className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-5 mb-6">
                <h3 className="text-[#111827] font-medium text-sm mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4 text-green-400" /> Thématiques cibles
                </h3>
                <div className="flex flex-wrap gap-2">
                  {prog.specialties_focus.map(s => (
                    <span key={s} className="px-3 py-1 bg-green-500/10 text-green-400 rounded-full text-xs">{s}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Quick actions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button onClick={() => { setTab('collaborators'); setShowAddCollab(true); }}
                className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-5 text-left hover:border-green-500/30 transition-all group">
                <UserPlus className="w-5 h-5 text-green-400 mb-2" />
                <p className="text-[#111827] font-medium text-sm">Ajouter des collaborateurs</p>
                <p className="text-[#6B7280] text-xs mt-1">Invitez vos équipes et attribuez des sessions</p>
              </button>
              <button onClick={() => navigate('/marketplace')}
                className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-5 text-left hover:border-[#D4AF37]/30 transition-all group">
                <Users className="w-5 h-5 text-[#D4AF37] mb-2" />
                <p className="text-[#111827] font-medium text-sm">Explorer les coachs</p>
                <p className="text-[#6B7280] text-xs mt-1">Voir les coachs disponibles dans l'Espace Coachs</p>
              </button>
            </div>
          </TabsContent>

          {/* Collaborators */}
          <TabsContent value="collaborators">
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <button data-testid="add-collab-btn" onClick={() => setShowAddCollab(true)}
                className="px-5 py-2.5 bg-green-500 text-[#111827] rounded-full text-sm font-medium hover:bg-green-400 transition-all flex items-center gap-2">
                <UserPlus className="w-4 h-4" /> Ajouter un collaborateur
              </button>
            </div>

            {/* Bulk import */}
            <div className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-5 mb-6">
              <h3 className="text-[#111827] font-medium text-sm mb-2 flex items-center gap-2">
                <Upload className="w-4 h-4 text-green-400" /> Import en masse
              </h3>
              <p className="text-[#6B7280] text-xs mb-3">Un collaborateur par ligne : <span className="text-zinc-300">Nom, Email, NbSessions</span></p>
              <textarea data-testid="bulk-import-text" value={bulkText} onChange={(e) => setBulkText(e.target.value)}
                placeholder={"Marie Martin, marie@entreprise.com, 5\nPierre Durand, pierre@entreprise.com, 3"}
                className="w-full px-3 py-2.5 bg-[#F8F7F4] border border-[#DDD9D0] rounded-xl text-[#111827] text-sm min-h-[80px] mb-3 focus:outline-none focus:border-green-500/40" />
              <button data-testid="bulk-import-btn" onClick={addBulkCollaborators} disabled={addingCollab || !bulkText.trim()}
                className="px-5 py-2 bg-green-500/10 text-green-400 rounded-full text-sm font-medium hover:bg-green-500/20 disabled:opacity-40 transition-all flex items-center gap-2">
                {addingCollab ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Importer
              </button>
            </div>

            {/* Collaborator list */}
            {collabs.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-10 h-10 text-[#6B7280] mx-auto mb-3" />
                <p className="text-[#6B7280] text-sm">Aucun collaborateur ajouté.</p>
              </div>
            ) : (
              <div className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#E8E5DE]">
                        <th className="text-left text-[#6B7280] text-xs font-medium px-4 py-3">Collaborateur</th>
                        <th className="text-center text-[#6B7280] text-xs font-medium px-4 py-3">Sessions</th>
                        <th className="text-center text-[#6B7280] text-xs font-medium px-4 py-3">Utilisées</th>
                        <th className="text-center text-[#6B7280] text-xs font-medium px-4 py-3">Restantes</th>
                        <th className="text-center text-[#6B7280] text-xs font-medium px-4 py-3">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {collabs.map(c => (
                        <tr key={c.id || c.email} className="border-b border-[#E8E5DE] last:border-0" data-testid={`collab-row-${c.email}`}>
                          <td className="px-4 py-3">
                            <p className="text-[#111827] font-medium">{c.name}</p>
                            <p className="text-[#6B7280] text-xs">{c.email}</p>
                          </td>
                          <td className="text-center text-zinc-300 px-4 py-3">{c.sessions_allocated}</td>
                          <td className="text-center text-[#D4AF37] px-4 py-3">{c.sessions_used || 0}</td>
                          <td className="text-center text-green-400 px-4 py-3">{c.sessions_allocated - (c.sessions_used || 0)}</td>
                          <td className="text-center px-4 py-3">
                            <Badge className={
                              c.status === 'active' ? 'bg-green-500/10 text-green-400' :
                              c.invitation_sent ? 'bg-blue-500/10 text-blue-400' :
                              'bg-zinc-500/10 text-[#6B7280]'
                            }>
                              {c.status === 'active' ? 'Actif' : c.invitation_sent ? 'Invité' : 'En attente'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Sessions */}
          <TabsContent value="sessions">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
              <StatCard label="Sessions totales" value={prog?.total_sessions || 0} icon={Calendar} />
              <StatCard label="Sessions validées" value={prog?.sessions_used || 0} icon={Check} color="#D4AF37" />
              <StatCard label="Sessions restantes" value={sessionsRemaining} icon={Clock} />
            </div>
            <div className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-5">
              <h3 className="text-[#111827] font-medium text-sm mb-4">Détail par collaborateur</h3>
              {collabs.length === 0 ? (
                <p className="text-[#6B7280] text-sm text-center py-6">Ajoutez des collaborateurs pour voir le suivi sessions.</p>
              ) : (
                <div className="space-y-3">
                  {collabs.map(c => {
                    const used = c.sessions_used || 0;
                    const allocated = c.sessions_allocated || 0;
                    const pct = allocated > 0 ? Math.round((used / allocated) * 100) : 0;
                    return (
                      <div key={c.id || c.email} className="bg-[#F0EDE6] rounded-xl p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[#111827] text-sm">{c.name}</span>
                          <span className="text-[#6B7280] text-xs">{used}/{allocated} sessions</span>
                        </div>
                        <div className="w-full h-2 bg-[#F0EDE6] rounded-full overflow-hidden">
                          <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Budget */}
          <TabsContent value="budget">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
              <StatCard label="Budget total" value={`${(prog?.budget_total || 0).toLocaleString('fr-FR')}€`} icon={Euro} />
              <StatCard label="Budget utilisé" value={`${(prog?.budget_used || 0).toLocaleString('fr-FR')}€`} icon={TrendingUp} color="#D4AF37" />
              <StatCard label="Budget restant" value={`${budgetRemaining.toLocaleString('fr-FR')}€`} icon={Euro} color="#22c55e" />
            </div>
            <div className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-5 mb-6">
              <h3 className="text-[#111827] font-medium text-sm mb-3">Répartition budgétaire</h3>
              <div className="w-full h-4 bg-[#F0EDE6] rounded-full overflow-hidden mb-3">
                <div className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F3D060] rounded-full transition-all"
                  style={{ width: `${prog?.budget_total ? Math.round((prog.budget_used / prog.budget_total) * 100) : 0}%` }} />
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-[#D4AF37] text-lg font-light">{(prog?.budget_used || 0).toLocaleString('fr-FR')}€</p>
                  <p className="text-[#6B7280] text-xs">Consommé</p>
                </div>
                <div>
                  <p className="text-[#6B7280] text-lg font-light">{prog?.max_rate_per_hour || 0}€/h</p>
                  <p className="text-[#6B7280] text-xs">Tarif max</p>
                </div>
                <div>
                  <p className="text-green-400 text-lg font-light">{budgetRemaining.toLocaleString('fr-FR')}€</p>
                  <p className="text-[#6B7280] text-xs">Disponible</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-5">
              <p className="text-[#6B7280] text-sm">
                Le budget est décrémenté <strong className="text-[#111827]">uniquement après validation</strong> de chaque session. 
                Les réservations en cours ne sont pas débitées.
              </p>
            </div>
          </TabsContent>

          {/* AI Matching */}
          <TabsContent value="matching">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Search panel */}
              <div className="lg:col-span-1 space-y-4">
                <div className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-5">
                  <h3 className="text-[#111827] font-medium text-sm mb-1 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#D4AF37]" /> Orientation IA
                  </h3>
                  <p className="text-[#6B7280] text-xs mb-4">Décrivez le besoin pour trouver le coach idéal.</p>

                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-[#6B7280] mb-1 block">Besoin du collaborateur *</label>
                      <textarea data-testid="match-need-input" value={matchNeed} onChange={(e) => setMatchNeed(e.target.value)}
                        placeholder="Ex: Développer sa posture de leader, améliorer la gestion d'équipe, préparer une prise de parole..."
                        className="w-full px-3 py-2.5 bg-[#F8F7F4] border border-[#DDD9D0] rounded-xl text-[#111827] text-sm min-h-[100px] focus:outline-none focus:border-green-500/40" />
                    </div>
                    <div>
                      <label className="text-xs text-[#6B7280] mb-1 block">Date souhaitée (optionnel)</label>
                      <Input data-testid="match-date-input" type="date" value={matchDate}
                        onChange={(e) => setMatchDate(e.target.value)}
                        className="bg-[#F8F7F4] border-[#DDD9D0] text-[#111827] rounded-xl" />
                    </div>

                    <button data-testid="run-matching-btn" onClick={runMatching} disabled={matching || !matchNeed.trim()}
                      className="w-full py-3 bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#111827] shadow-[0_2px_8px_rgba(180,150,40,0.25)] rounded-full font-medium text-sm hover:bg-[#F3D060] disabled:opacity-40 transition-all flex items-center justify-center gap-2">
                      {matching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      {matching ? 'Analyse en cours...' : 'Trouver un coach'}
                    </button>
                  </div>

                  {/* Program context info */}
                  {prog?.specialties_focus?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-[#E8E5DE]">
                      <p className="text-[#6B7280] text-xs mb-2">Thématiques du programme :</p>
                      <div className="flex flex-wrap gap-1">
                        {prog.specialties_focus.map(s => (
                          <span key={s} className="px-2 py-0.5 bg-green-500/10 text-green-400 rounded text-[10px]">{s}</span>
                        ))}
                      </div>
                      {prog?.max_rate_per_hour && (
                        <p className="text-[#6B7280] text-xs mt-2">Budget max : <span className="text-[#D4AF37]">{prog.max_rate_per_hour}€/h</span></p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Results panel */}
              <div className="lg:col-span-2">
                {matchResults === null ? (
                  <div className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-12 text-center">
                    <Sparkles className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                    <p className="text-[#6B7280] text-sm">Décrivez un besoin pour obtenir des recommandations personnalisées.</p>
                  </div>
                ) : matchResults.length === 0 ? (
                  <div className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-12 text-center">
                    <Users className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                    <p className="text-[#6B7280] text-sm">Aucun coach ne correspond à ces critères.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-[#6B7280] text-xs mb-2">{matchResults.length} coach(s) recommandé(s)</p>
                    {matchResults.map((coach, idx) => (
                      <div key={coach.coach_id}
                        data-testid={`match-result-${idx}`}
                        className="bg-white border border-[#E8E5DE] rounded-xl shadow-sm p-5 hover:border-green-500/20 transition-all">
                        <div className="flex items-start gap-4">
                          {/* Rank */}
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm ${
                            idx === 0 ? 'bg-[#D4AF37]/15 text-[#D4AF37]' :
                            idx === 1 ? 'bg-zinc-500/10 text-zinc-300' :
                            'bg-[#F0EDE6] text-[#6B7280]'
                          }`}>
                            #{idx + 1}
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="text-[#111827] font-medium">{coach.display_name}</h4>
                              <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-[10px]">
                                {coach.score}/100
                              </Badge>
                              {coach.rating_avg > 0 && (
                                <span className="flex items-center gap-0.5 text-[#D4AF37] text-xs">
                                  <Star className="w-3 h-3 fill-current" /> {coach.rating_avg}
                                </span>
                              )}
                            </div>

                            {/* AI Reason */}
                            <p className="text-green-400/80 text-sm mb-2 italic">{coach.reason}</p>

                            {/* Specialties */}
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {(coach.specialties || []).map(s => (
                                <span key={s} className="px-2 py-0.5 bg-[#F0EDE6] text-[#6B7280] rounded text-xs">{s}</span>
                              ))}
                            </div>

                            {/* Meta */}
                            <div className="flex items-center gap-4 text-xs text-[#6B7280]">
                              <span>{coach.hourly_rate}€/h</span>
                              <span>{coach.session_duration} min</span>
                              <span>{coach.total_sessions} session(s)</span>
                            </div>
                          </div>

                          {/* Action */}
                          <button
                            onClick={() => navigate(`/marketplace/coach/${coach.coach_id}`)}
                            className="flex-shrink-0 px-4 py-2 bg-green-500/10 text-green-400 rounded-full text-xs font-medium hover:bg-green-500/20 transition-all">
                            Voir le profil
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Collaborator Dialog */}
      <Dialog open={showAddCollab} onOpenChange={setShowAddCollab}>
        <DialogContent className="bg-white border-[#DDD9D0] text-[#111827] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#111827] text-lg">Ajouter un collaborateur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm text-zinc-300 mb-1.5 block">Nom complet *</label>
              <Input data-testid="collab-name" value={collabForm.name}
                onChange={(e) => setCollabForm({ ...collabForm, name: e.target.value })}
                placeholder="Ex: Marie Martin" className="bg-[#F8F7F4] border-[#DDD9D0] text-[#111827] rounded-xl" />
            </div>
            <div>
              <label className="text-sm text-zinc-300 mb-1.5 block">Email *</label>
              <Input data-testid="collab-email" value={collabForm.email}
                onChange={(e) => setCollabForm({ ...collabForm, email: e.target.value })}
                placeholder="marie@entreprise.com" className="bg-[#F8F7F4] border-[#DDD9D0] text-[#111827] rounded-xl" />
            </div>
            <div>
              <label className="text-sm text-zinc-300 mb-1.5 block">Sessions attribuées *</label>
              <Input data-testid="collab-sessions" type="number" value={collabForm.sessions_allocated}
                onChange={(e) => setCollabForm({ ...collabForm, sessions_allocated: parseInt(e.target.value) || 1 })}
                className="bg-[#F8F7F4] border-[#DDD9D0] text-[#111827] rounded-xl" min="1" />
            </div>
            <button data-testid="confirm-add-collab" onClick={addCollaborator} disabled={addingCollab}
              className="w-full py-3 bg-green-500 text-[#111827] rounded-full font-medium text-sm hover:bg-green-400 disabled:opacity-50 transition-all flex items-center justify-center gap-2">
              {addingCollab ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Ajouter et envoyer l'invitation
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
